import type { FileCategory, IFileItem, GetPresignedURLResponse } from "@/types/files";

export interface UploadSingleFileState {
  prepared: boolean;
  preparing: boolean;
  uploading: boolean;
  success: boolean;
  error: boolean;
  progress: number;
  data: IFileItem | null;
  message: string;
}

export function uploadSingleFile(id: string) {
  const file = ref<null | File>(null);

  const state = ref<UploadSingleFileState>({
    prepared: false,
    preparing: false,
    uploading: false,
    success: false,
    error: false,
    progress: 0,
    data: null,
    message: "",
  });

  async function upload(
    theFile: File | null,
    _type?: FileCategory,
    progressCallback?: (event: any) => any,
    presignedData?: GetPresignedURLResponse
  ) {
    try {
      if (!theFile) {
        throw new Error("Missing File");
      }

      file.value = theFile;
      state.value.preparing = true;

      let presignedResponse = presignedData;

      // If no presigned data provided, fetch individually
      if (!presignedResponse) {
        const isCustom = ["contacts", "logo", "task-image", "task-video"].includes(
          `${_type}`
        );
        let type = _type;
        if (!type && file.value.type?.startsWith("image/")) type = "mediaAssets";
        if (!type && file.value.type?.startsWith("video/")) type = "mediaAssets";
        if (isCustom) type = undefined;
        const { data } = await getPreSignedUploadUrl([
          {
            name: file.value.name,
            type: type,
            uploadFileType: isCustom
              ? (_type as "contacts" | "logo" | "task-image" | "task-video")
              : undefined,
            contentLength: file.value.size,
            contentType: file.value.type,
          },
        ]);
        presignedResponse = data.response?.[0];
      }

      const { presigned, create } = presignedResponse || {};

      if (!presigned) {
        throw new Error("There was a problem getting the presigned URL.");
      }

      // state
      state.value.data = create;
      state.value.prepared = true;
      state.value.preparing = false;
      state.value.uploading = true;

      // setup form data to post to s3
      const formData = new FormData();
      // add all fields
      Object.entries(presigned.fields).forEach(([key, value]: any) => {
        formData.append(key, value);
      });
      // add file
      formData.append("file", file.value);

      // save to s3
      const response = await uploadFile(presigned.url, formData, event => {
        if (event.progress) {
          state.value.progress = event.progress;
        }
        if (typeof progressCallback === "function") {
          progressCallback(event);
        }
      });

      if (response.status !== 204) {
        throw new Error("There was a problem uploading.");
      }

      state.value.success = true;

      return create;
    } catch (e) {
      state.value.error = true;
      state.value.message = (e as Error).message;
    } finally {
      state.value.uploading = false;
      state.value.prepared = false;
      state.value.preparing = false;
    }
  }

  return {
    id,
    upload,
    state,
  };
}
